/* DoctorComposerPage — Phase 4B (iter320 redesign).
 *
 * 7-step wizard centred on PATIENT-saved clinical phenotypes:
 *   1) Pick patient
 *   2) Patient context overview (read-only — measurements, labs, calculators, DIL)
 *   3) Clinical phenotypes (PAM50, BI-RADS, Хашимото, MAFLD, IBD ... — the
 *      24+ conditions saved in patient profile.phenotype_subtypes; doctor
 *      multi-selects which of those should drive the plan)
 *   4) Epigenetic map (read-only display of patient's saved map)
 *   5) Pathway content review (foods, supplements, physical activity, mind-body)
 *   6) Pre-AI summary — single page showing ALL data that goes into the AI
 *   7) AI plan + chat
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import DoctorShell from "@/components/doctor/DoctorShell";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import {
  Wand2, ArrowLeft, ArrowRight, CheckCircle2, FileDown, Mail, Save,
  Loader2, Activity, ChefHat, Pill, Brain, Search, Users, HeartPulse, Dumbbell,
  Beaker, FileText, Send, Sparkles, Dna, ClipboardList, Calculator, X,
} from "lucide-react";
import PhenotypeClassifier from "@/components/PhenotypeClassifier";
import EpigeneticMapPanel from "@/components/EpigeneticMapPanel";

const STEPS = [
  { key: "select",    label_bg: "1. Пациент",     label_en: "1. Patient" },
  { key: "context",   label_bg: "2. Контекст",     label_en: "2. Context" },
  { key: "phenotype", label_bg: "3. Фенотипи",     label_en: "3. Phenotypes" },
  { key: "epigenetic",label_bg: "4. Епигенетика",  label_en: "4. Epigenetics" },
  { key: "pathway",   label_bg: "5. Препоръки",    label_en: "5. Recommendations" },
  { key: "summary",   label_bg: "6. Преглед",       label_en: "6. Review" },
  { key: "ai",        label_bg: "7. AI план",      label_en: "7. AI plan" },
];

const DOCTOR_ROLES = ["admin", "clinician"];

export default function DoctorComposerPage() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { patientId: initialPatientId } = useParams();
  const bg = language === "bg";

  const [stepIdx, setStepIdx] = useState(0);

  // Step 1
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState(initialPatientId || null);

  // Step 2
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  // Step 3 — clinical phenotype catalog + selection
  const [phenotypeConditions, setPhenotypeConditions] = useState([]);
  const [selectedPhenotypes, setSelectedPhenotypes] = useState([]);
  const [enginePathways, setEnginePathways] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Step 4 — epigenetic map (read-only)
  const [epigeneticData, setEpigeneticData] = useState(null);

  // Step 5 — derived recommendations
  const [pathwayPreview, setPathwayPreview] = useState(null);
  const [pathwayLoading, setPathwayLoading] = useState(false);

  // configuration (always available; default values exposed in step 6)
  const [planDays, setPlanDays] = useState(7);
  const [includeSupplements, setIncludeSupplements] = useState(true);
  const [includeMindBody, setIncludeMindBody] = useState(true);
  const [includePhysicalActivity, setIncludePhysicalActivity] = useState(true);

  // Step 7 — AI session
  const [aiResponse, setAiResponse] = useState("");
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [savedPlanId, setSavedPlanId] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!DOCTOR_ROLES.includes(user.role)) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // iter322 — Formula transparency modal
  const [formulasOpen, setFormulasOpen] = useState(false);
  const [formulas, setFormulas] = useState(null);
  const openFormulas = useCallback(async () => {
    setFormulasOpen(true);
    if (formulas) return;
    try {
      const { data } = await axios.get(`${API}/doctor/composer/formulas`);
      setFormulas(data);
    } catch { /* ignore */ }
  }, [API, formulas]);

  // Patients list
  const fetchPatients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doctor/patients${search ? `?q=${encodeURIComponent(search)}` : ""}`);
      setPatients(data.patients || []);
    } catch { setPatients([]); }
  }, [API, search]);
  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  // Phenotype catalog
  useEffect(() => {
    axios.get(`${API}/doctor/composer/clinical-phenotypes`)
      .then(({ data }) => {
        setPhenotypeConditions(data.phenotype_conditions || []);
        setEnginePathways(data.engine_pathways || []);
      })
      .catch(() => { setPhenotypeConditions([]); setEnginePathways([]); });
  }, [API]);

  // Patient context
  useEffect(() => {
    if (!selectedPatientId) { setContext(null); return; }
    let cancelled = false;
    setContextLoading(true);
    axios.get(`${API}/doctor/composer/patient-context/${selectedPatientId}`)
      .then(({ data }) => {
        if (cancelled) return;
        setContext(data);
        // iter322 — Pre-select ONLY saved patient subtypes. Никакви халюцинирани
        // defaults: ако пациентът няма запазени фенотипи, лекарят сам трябва
        // да избере какво да управлява плана.
        const savedKeys = (data.phenotype_subtypes_labelled || []).map(p => p.key);
        if (savedKeys.length > 0) {
          setSelectedPhenotypes(prev => prev.length > 0 ? prev : savedKeys);
        }
      })
      .catch(() => { if (!cancelled) setContext(null); })
      .finally(() => { if (!cancelled) setContextLoading(false); });
    return () => { cancelled = true; };
  }, [API, selectedPatientId]);

  // Epigenetic map — fetch latest on patient change
  useEffect(() => {
    if (!selectedPatientId) { setEpigeneticData(null); return; }
    setEpigeneticData(context?.epigenetic_map || null);
  }, [selectedPatientId, context]);

  // Pathway preview
  const generatePathwayPreview = useCallback(async () => {
    if (!selectedPatientId) return;
    setPathwayLoading(true);
    try {
      const { data } = await axios.post(`${API}/doctor/composer/run`, {
        patient_id: selectedPatientId,
        plan_days: planDays,
        include_supplements: includeSupplements,
        include_mindbody: includeMindBody,
        include_physical_activity: includePhysicalActivity,
        clinical_phenotypes: selectedPhenotypes.length > 0 ? selectedPhenotypes : null,
      });
      setPathwayPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка" : "Error"));
    } finally {
      setPathwayLoading(false);
    }
  }, [API, selectedPatientId, planDays, includeSupplements, includeMindBody, includePhysicalActivity, selectedPhenotypes, bg]);

  // AI generate
  const handleAIGenerate = async () => {
    setAiLoading(true);
    setAiResponse("");
    setChatHistory([]);
    try {
      const { data } = await axios.post(`${API}/doctor/composer/ai-meal-plan`, {
        patient_id: selectedPatientId,
        plan_days: planDays,
        include_supplements: includeSupplements,
        include_mindbody: includeMindBody,
        include_physical_activity: includePhysicalActivity,
        clinical_phenotypes: selectedPhenotypes.length > 0 ? selectedPhenotypes : null,
      }, { timeout: 120000 });
      setAiResponse(data.response_markdown || "");
      setAiSessionId(data.session_id);
      setChatHistory([{ role: "ai", text: data.response_markdown || "" }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "AI грешка" : "AI error"));
    } finally { setAiLoading(false); }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || !aiSessionId) return;
    const msg = chatInput.trim();
    setChatHistory(h => [...h, { role: "user", text: msg }]);
    setChatInput("");
    setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/doctor/composer/ai-chat`, {
        session_id: aiSessionId, user_message: msg, patient_id: selectedPatientId,
      }, { timeout: 120000 });
      setChatHistory(h => [...h, { role: "ai", text: data.response_markdown || "" }]);
    } catch (e) { toast.error(e?.response?.data?.detail || (bg ? "AI грешка" : "AI error")); }
    finally { setAiLoading(false); }
  };

  const handleSave = async () => {
    if (!pathwayPreview) return;
    try {
      const { data } = await axios.post(`${API}/doctor/composer/save`, {
        composer_payload: { ...pathwayPreview, ai_meal_plan_markdown: aiResponse, ai_session_id: aiSessionId },
        note: "",
      });
      setSavedPlanId(data.plan_id);
      toast.success(bg ? "Планът е запазен" : "Plan saved");
    } catch (e) { toast.error(e?.response?.data?.detail || (bg ? "Грешка" : "Save failed")); }
  };

  const handlePdf = async () => {
    setPdfDownloading(true);
    try {
      const resp = await axios.post(`${API}/doctor/composer/run/pdf`, {
        patient_id: selectedPatientId, plan_days: planDays,
        include_supplements: includeSupplements, include_mindbody: includeMindBody,
        include_physical_activity: includePhysicalActivity,
        clinical_phenotypes: selectedPhenotypes.length > 0 ? selectedPhenotypes : null,
      }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `nutramilo-plan-${selectedPatientId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(bg ? "PDF е изтеглен" : "PDF downloaded");
    } catch { toast.error(bg ? "PDF грешка" : "PDF error"); }
    finally { setPdfDownloading(false); }
  };

  const handleEmail = async () => {
    setEmailSending(true);
    try {
      const { data } = await axios.post(`${API}/doctor/composer/email`, {
        patient_id: selectedPatientId, plan_days: planDays,
        include_supplements: includeSupplements, include_mindbody: includeMindBody,
        include_physical_activity: includePhysicalActivity,
        clinical_phenotypes: selectedPhenotypes.length > 0 ? selectedPhenotypes : null,
      });
      toast.success(bg ? `Изпратено до ${data.sent_to}` : `Sent to ${data.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || (bg ? "Грешка" : "Email failed")); }
    finally { setEmailSending(false); }
  };

  const selectedPatient = useMemo(() => patients.find(p => p.id === selectedPatientId), [patients, selectedPatientId]);
  const cardCls = `rounded-2xl border ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"}`;

  const canGoNext =
    stepIdx === 0 ? !!selectedPatientId :
    stepIdx === 2 ? selectedPhenotypes.length > 0 :
    true;

  const goNext = () => {
    const next = Math.min(STEPS.length - 1, stepIdx + 1);
    setStepIdx(next);
    if (next === 4) generatePathwayPreview();
    if (next === 5) generatePathwayPreview();
  };

  const savedConditionKeys = useMemo(
    () => (context?.phenotype_subtypes_labelled || []).map(p => p.key),
    [context]
  );

  return (
    <>
      <SEO
        title={bg ? "Клиничен план — Доктор портал" : "Clinical Plan — Doctor Portal"}
        description={bg ? "Phenotype-driven AI план базиран на пациентските субтипове и епигенетична карта."
          : "Phenotype-driven AI plan from patient clinical subtypes & epigenetic map."}
        path="/doctor/composer"
      />
      <DoctorShell>
        <div className={`flex-1 ${isDark ? "bg-[hsl(222,47%,6%)]" : "bg-[#F8FAFC]"} min-h-screen`}
          data-testid="doctor-composer-page">
          {/* Header */}
          <header className={`px-6 py-4 border-b ${isDark ? "border-white/5 bg-[hsl(222,47%,8%)]" : "border-gray-200 bg-white"}`}>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/doctor")}
                className={`p-2 rounded-lg ${isDark ? "hover:bg-white/5 text-gray-400" : "hover:bg-gray-100 text-gray-600"}`}
                data-testid="composer-back-btn" aria-label="Back">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-lg">
                <Wand2 className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className={`text-lg sm:text-xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {bg ? "Клиничен план" : "Clinical Plan"}
                </h1>
                <p className={`text-[11px] sm:text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {bg ? "Контекст → клинични субтипове → епигенетика → препоръки → преглед → AI"
                     : "Context → clinical subtypes → epigenetics → recommendations → summary → AI"}
                </p>
              </div>
              {selectedPatient && (
                <div className={`hidden sm:block text-right text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {bg ? "Пациент: " : "Patient: "}
                  <b className={isDark ? "text-white" : "text-[#1A1A2E]"}>{selectedPatient.name || selectedPatient.email}</b>
                </div>
              )}
              <button type="button" onClick={openFormulas}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11px] font-semibold border transition-colors flex-shrink-0
                  ${isDark ? "bg-cyan-500/10 border-cyan-400/30 text-cyan-200 hover:bg-cyan-500/20"
                           : "bg-cyan-50 border-cyan-200 text-cyan-800 hover:bg-cyan-100"}`}
                data-testid="composer-formulas-btn"
                title={bg ? "Покажи всички формули" : "Show all formulas"}>
                <Calculator className="w-3.5 h-3.5" />
                {bg ? "Формули" : "Formulas"}
              </button>
            </div>

            <div className="mt-4 flex items-center gap-2 overflow-x-auto" data-testid="composer-stepper">
              {STEPS.map((s, i) => {
                const isActive = i === stepIdx;
                const isDone = i < stepIdx;
                return (
                  <button key={s.key} type="button"
                    onClick={() => i <= stepIdx && setStepIdx(i)}
                    disabled={i > stepIdx}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-semibold transition-colors
                      ${isActive
                        ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow"
                        : isDone
                          ? (isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700")
                          : (isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500")}
                      ${i > stepIdx ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    data-testid={`composer-step-${s.key}`}>
                    {isDone && <CheckCircle2 className="w-3 h-3" />}
                    {bg ? s.label_bg : s.label_en}
                  </button>
                );
              })}
            </div>
          </header>

          <main className="px-4 sm:px-6 py-6 max-w-5xl mx-auto">
            {/* STEP 1 — Pick patient */}
            {stepIdx === 0 && (
              <section className="space-y-4" data-testid="composer-step-select-body">
                <div className={`${cardCls} p-4`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-700"}`} />
                    <h2 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{bg ? "Избери пациент" : "Select patient"}</h2>
                  </div>
                  <div className="relative mb-3">
                    <Search className={`absolute left-3 top-2.5 w-4 h-4 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                    <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder={bg ? "Търсене..." : "Search..."}
                      className={`w-full pl-9 pr-3 h-10 rounded-xl text-sm border outline-none focus:ring-2 ring-violet-500/30
                        ${isDark ? "bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                                 : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
                      data-testid="composer-patient-search" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto">
                    {patients.map(p => (
                      <button key={p.id} type="button" onClick={() => setSelectedPatientId(p.id)}
                        className={`flex items-start gap-2.5 p-3 rounded-xl border text-left
                          ${selectedPatientId === p.id
                            ? (isDark ? "bg-violet-500/15 border-violet-400/40" : "bg-violet-50 border-violet-300")
                            : (isDark ? "bg-white/5 border-white/5 hover:border-white/15"
                                     : "bg-white border-gray-200 hover:border-gray-300")}`}
                        data-testid={`composer-patient-${p.id}`}>
                        <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white text-xs font-bold">
                          {(p.name || p.email || "?").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm font-semibold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{p.name || "—"}</div>
                          <div className={`text-[11px] truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}>{p.email || "—"}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* STEP 2 — Patient context */}
            {stepIdx === 1 && (
              <section className="space-y-3" data-testid="composer-step-context-body">
                {contextLoading && <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" />{bg ? "Извличане..." : "Loading..."}</div>}
                {context && !contextLoading && (
                  <>
                    <Block title={bg ? "Лична + физическа" : "Personal + Body"} icon={HeartPulse} color="#EC4899" cardCls={cardCls} isDark={isDark}>
                      <Grid items={[
                        [bg ? "Възраст" : "Age", context.personal?.age],
                        [bg ? "Пол" : "Gender", context.personal?.gender],
                        [bg ? "Тегло (kg)" : "Weight (kg)", context.body?.weight],
                        [bg ? "Височина (cm)" : "Height (cm)", context.body?.height],
                        [bg ? "Цел" : "Goal", context.personal?.goal || context.body?.goal],
                        [bg ? "Талия (cm)" : "Waist (cm)", context.measurements?.waist],
                      ]} isDark={isDark} />
                    </Block>
                    <Block title={bg ? "Здравословни условия" : "Health conditions"} icon={Activity} color="#F59E0B" cardCls={cardCls} isDark={isDark}>
                      <div className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        <p><b>{bg ? "Условия: " : "Conditions: "}</b>{context.health?.currentConditions || "—"}</p>
                        <p><b>{bg ? "Лекарства: " : "Medications: "}</b>{context.health?.medications || "—"}</p>
                        <p><b>{bg ? "Алергии: " : "Allergies: "}</b>{context.health?.allergies || "—"}</p>
                      </div>
                    </Block>
                    <Block title={bg ? "Лабораторни маркери" : "Lab markers"} icon={Beaker} color="#0EA5E9" cardCls={cardCls} isDark={isDark}>
                      <Grid items={Object.entries(context.lab_values || {}).slice(0, 12)} isDark={isDark}
                        empty={bg ? "Няма данни" : "No data"} />
                    </Block>

                    {/* iter321 — Health indices (US Navy / Deurenberg / BMR / TDEE / Personalized DIL) */}
                    {context.health_indices?.body_composition && (
                      <Block title={bg ? "Здравни индекси (формули от Nutramilo legacy bundle)" : "Health indices (legacy formulas)"}
                        icon={HeartPulse} color="#06B6D4" cardCls={cardCls} isDark={isDark}>
                        <Grid items={[
                          ["BMI", context.health_indices.body_composition.bmi],
                          [bg ? "Body fat %" : "Body fat %", `${context.health_indices.body_composition.body_fat_percent} (${context.health_indices.body_composition.method_used})`],
                          [bg ? "Lean mass" : "Lean mass", `${context.health_indices.body_composition.lean_mass_kg} kg`],
                          [bg ? "Fat mass" : "Fat mass", `${context.health_indices.body_composition.fat_mass_kg} kg`],
                          ["BMR", context.health_indices.energy?.bmr ? `${context.health_indices.energy.bmr} kcal` : "—"],
                          ["TDEE", context.health_indices.energy?.tdee ? `${context.health_indices.energy.tdee} kcal` : "—"],
                          [bg ? "Калории-цел" : "Cal target", context.health_indices.energy?.final_calories ? `${context.health_indices.energy.final_calories} kcal` : "—"],
                          [bg ? "Персонализиран DIL" : "Personalized DIL", context.health_indices.energy?.personalized_dil],
                          [bg ? "Естрогенен риск" : "Estrogen risk", (context.health_indices.body_composition.estrogen_risk || {}).label_bg],
                        ]} isDark={isDark} />
                        {(context.health_indices.body_composition.estrogen_risk || {}).note_bg && (
                          <p className={`text-[11px] mt-2 italic ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            {context.health_indices.body_composition.estrogen_risk.note_bg}
                          </p>
                        )}
                      </Block>
                    )}
                    <Block title={bg ? "Здравни калкулатори" : "Health calculators"} icon={Sparkles} color="#A855F7" cardCls={cardCls} isDark={isDark}>
                      {(context.calculator_results || []).length === 0 && <span className="text-xs text-gray-500">{bg ? "Няма" : "—"}</span>}
                      <div className="space-y-1">
                        {(context.calculator_results || []).slice(0, 8).map((c, i) => (
                          <div key={i} className={`text-[12px] flex justify-between ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            <span className="font-mono uppercase text-[10px]">{c.type}</span>
                            <span className="font-bold">{c.result?.risk_percent ?? c.result?.score ?? c.result?.label_bg ?? "—"}</span>
                          </div>
                        ))}
                      </div>
                    </Block>
                    <Block title="DIL · Биоложка възраст" icon={FileText} color="#10B981" cardCls={cardCls} isDark={isDark}>
                      <Grid items={[
                        [bg ? "Хранения (7д)" : "Meals (7d)", context.dil?.meals_last_7d],
                        [bg ? "Среден DIL" : "Avg DIL", context.dil?.avg_dil],
                        [bg ? "Средни kcal" : "Avg kcal", context.dil?.avg_kcal],
                        [bg ? "Биол. възраст" : "Bio age", context.biological_age?.biological_age],
                        ["Δ Age", context.biological_age?.age_acceleration],
                      ]} isDark={isDark} />
                    </Block>
                  </>
                )}
              </section>
            )}

            {/* STEP 3 — Phenotype select */}
            {stepIdx === 2 && (
              <section className="space-y-3" data-testid="composer-step-phenotype-body">
                {/* Saved subtypes from patient profile */}
                <div className={`${cardCls} p-4`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                        {bg ? "Клинични фенотипи (от профила на пациента)" : "Clinical phenotypes (from patient profile)"}
                      </h2>
                      <p className={`text-[12px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        {bg ? "PAM50 · BI-RADS · Хашимото · MAFLD · IBD · PCOS · T2DM · Депресия · MS · Мигрена ..."
                           : "PAM50 · BI-RADS · Hashimoto · MAFLD · IBD · PCOS · T2DM · Depression · MS · Migraine ..."}
                      </p>
                    </div>
                    {selectedPatientId && (
                      <button type="button"
                        onClick={() => {
                          // refetch context after PhenotypeClassifier saves on behalf of the patient
                          axios.get(`${API}/doctor/composer/patient-context/${selectedPatientId}`)
                            .then(({ data }) => setContext(data)).catch(() => {});
                        }}
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11px] font-semibold border
                          ${isDark ? "bg-white/5 border-white/10 text-violet-300 hover:bg-white/10"
                                   : "bg-white border-gray-200 text-violet-700 hover:bg-gray-50"}`}
                        data-testid="composer-refresh-phenotypes">
                        <CheckCircle2 className="w-3 h-3" />
                        {bg ? "Опресни след промяна" : "Refresh"}
                      </button>
                    )}
                  </div>

                  {savedConditionKeys.length === 0 && (
                    <div className={`p-3 rounded-xl text-[12px]
                      ${isDark ? "bg-amber-500/10 text-amber-200 border border-amber-400/20"
                               : "bg-amber-50 text-amber-800 border border-amber-100"}`}>
                      {bg ? "Пациентът няма запазени клинични фенотипи. Добави от профила на пациента (Дашборд → Клинични фенотипи)."
                         : "Patient has no saved clinical phenotypes yet. Add them from the patient's dashboard."}
                    </div>
                  )}

                  {(context?.phenotype_subtypes_labelled || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {context.phenotype_subtypes_labelled.map(p => {
                        const checked = selectedPhenotypes.includes(p.key);
                        return (
                          <button key={p.key} type="button"
                            onClick={() => setSelectedPhenotypes(prev => checked ? prev.filter(k => k !== p.key) : [...prev, p.key])}
                            className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition-colors
                              ${checked
                                ? (isDark ? "bg-pink-500/15 border-pink-400/40" : "bg-pink-50 border-pink-300")
                                : (isDark ? "bg-white/5 border-white/5 hover:border-white/15"
                                         : "bg-white border-gray-200 hover:border-gray-300")}`}
                            data-testid={`composer-phenotype-${p.key}`}>
                            <span className={`w-5 h-5 mt-0.5 flex-shrink-0 rounded-md border flex items-center justify-center
                              ${checked ? "bg-gradient-to-br from-pink-500 to-fuchsia-600 border-transparent" : (isDark ? "border-white/20" : "border-gray-300")}`}>
                              {checked && <CheckCircle2 className="w-4 h-4 text-white" />}
                            </span>
                            <div className="min-w-0">
                              <div className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{p.label_bg}</div>
                              <SubtypeDetail data={p.data} isDark={isDark} />
                              {(p.pathways || []).length > 0 && (
                                <div className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                                  → {p.pathways.join(", ")}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className={`mt-3 text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {bg ? "Избрани: " : "Selected: "}{selectedPhenotypes.length}
                  </div>
                </div>

                {/* iter321 — Embedded PhenotypeClassifier (acts on patient via target_user_id).
                    Doctor can add/remove subtypes inline without leaving the wizard. */}
                {selectedPatientId && (
                  <div data-testid="composer-embedded-phenotype-classifier">
                    <PhenotypeClassifier targetUserId={selectedPatientId} />
                  </div>
                )}

                {/* Advanced — engine pathways (legacy) */}
                <div className={`${cardCls} p-4`}>
                  <button type="button"
                    onClick={() => setShowAdvanced(s => !s)}
                    className={`flex items-center gap-2 text-[11px] font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}
                    data-testid="composer-toggle-advanced">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px]
                      ${showAdvanced ? "bg-violet-600 border-violet-600 text-white" : (isDark ? "border-white/20" : "border-gray-300")}`}>
                      {showAdvanced ? "−" : "+"}
                    </span>
                    {bg ? "Разширено: добави pathway директно" : "Advanced: add pathway directly"}
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {enginePathways.map(p => {
                        const checked = selectedPhenotypes.includes(p.key);
                        return (
                          <button key={p.key} type="button"
                            onClick={() => setSelectedPhenotypes(prev => checked ? prev.filter(k => k !== p.key) : [...prev, p.key])}
                            className={`p-2 rounded-lg text-[11px] border text-left
                              ${checked
                                ? (isDark ? "bg-violet-500/15 border-violet-400/40 text-violet-200" : "bg-violet-50 border-violet-300 text-violet-800")
                                : (isDark ? "bg-white/5 border-white/5 text-gray-300 hover:bg-white/10"
                                         : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50")}`}
                            data-testid={`composer-pathway-${p.key}`}>
                            {p.label_bg}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* STEP 4 — Epigenetic map */}
            {stepIdx === 3 && (
              <section className="space-y-3" data-testid="composer-step-epigenetic-body">
                <div className={`${cardCls} p-4`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-pink-500 to-violet-600 text-white">
                      <Dna className="w-4 h-4" />
                    </div>
                    <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Епигенетична карта" : "Epigenetic map"}
                    </h2>
                  </div>
                  <p className={`text-[12px] mb-3 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {bg ? "Според GDPR епигенетичната карта се анализира от лекар. Можеш да я генерираш / прегенерираш директно тук — резултатите се записват в пациентския профил."
                       : "Per GDPR, epigenetic maps are analyzed by a doctor. Generate / regenerate here — saved on patient's profile."}
                  </p>
                  {epigeneticData && (
                    <div className={`p-3 rounded-xl mb-3 ${isDark ? "bg-violet-500/10 border border-violet-400/20" : "bg-violet-50 border border-violet-100"}`}>
                      <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                        {bg ? "Текуща карта (от профила)" : "Current saved map"}
                      </div>
                      <Grid items={[
                        [bg ? "Score" : "Score", epigeneticData.score],
                        [bg ? "Биологична възраст" : "Biological age", epigeneticData.biological_age],
                        [bg ? "Δ Acceleration" : "Δ Acceleration", epigeneticData.age_acceleration],
                        [bg ? "Pathway risk" : "Pathway risk", epigeneticData.pathway_risk],
                      ].filter(([, v]) => v !== undefined && v !== null)} isDark={isDark} empty="—" />
                    </div>
                  )}
                </div>

                {/* iter321 — Embedded EpigeneticMapPanel (acts on patient via targetUserId). */}
                {selectedPatientId && (
                  <div data-testid="composer-embedded-epigenetic-panel">
                    <EpigeneticMapPanel targetUserId={selectedPatientId} />
                  </div>
                )}
              </section>
            )}

            {/* STEP 5 — Pathway recommendations */}
            {stepIdx === 4 && (
              <section className="space-y-3" data-testid="composer-step-pathway-body">
                {pathwayLoading && <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" />{bg ? "Изчисляване..." : "Loading..."}</div>}
                {pathwayPreview && !pathwayLoading && (
                  <>
                    {pathwayPreview.triggering_conditions?.length > 0 && (
                      <div className={`${cardCls} p-3`}>
                        <div className={`text-[11px] mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {bg ? "Активни клинични субтипове (управляват плана):" : "Active clinical subtypes (driving the plan):"}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {pathwayPreview.triggering_conditions.map(c => (
                            <span key={c.key}
                              className={`text-[11px] px-2.5 py-1 rounded-full
                                ${isDark ? "bg-pink-500/15 text-pink-200 border border-pink-400/20"
                                        : "bg-pink-50 text-pink-800 border border-pink-100"}`}>
                              {c.label_bg}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <Block title={bg ? "Храни — emphasize / avoid" : "Foods — emphasize / avoid"} icon={ChefHat} color="#10B981" cardCls={cardCls} isDark={isDark}>
                      <div className="space-y-3">
                        {(pathwayPreview.pathway_picks || []).map(p => (
                          <div key={p.pathway} className="text-sm">
                            <div className={`font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>{p.label_bg}</div>
                            <div className={`text-[12px] mt-0.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>✓ {(p.emphasize || []).slice(0, 6).join(", ")}</div>
                            <div className={`text-[12px] ${isDark ? "text-rose-300" : "text-rose-700"}`}>✗ {(p.avoid || []).slice(0, 5).join(", ")}</div>
                            {(p.kegg_pathways || []).length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {p.kegg_pathways.map(k => (
                                  <a key={k.id}
                                    href={`https://www.kegg.jp/entry/${k.id}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className={`text-[10px] font-mono px-1.5 py-0.5 rounded
                                      ${isDark ? "bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25"
                                              : "bg-cyan-50 text-cyan-700 hover:bg-cyan-100"}`}
                                    title={bg ? k.name_bg : k.name_en}>
                                    KEGG {k.id} · {bg ? k.name_bg : k.name_en}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Block>

                    {(pathwayPreview.supplements_recommendations || []).length > 0 && (
                      <Block title={bg ? "Добавки" : "Supplements"} icon={Pill} color="#A855F7" cardCls={cardCls} isDark={isDark}>
                        <div className="space-y-2">
                          {pathwayPreview.supplements_recommendations.map((s, i) => (
                            <div key={i} className={`p-2.5 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{s.name_bg}</span>
                                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-700"}`}>{s.dose_bg}</span>
                              </div>
                              <p className={`text-[11px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{s.why_bg}</p>
                            </div>
                          ))}
                        </div>
                      </Block>
                    )}

                    {(pathwayPreview.physical_activity_recommendations || []).length > 0 && (
                      <Block title={bg ? "Физически усилия" : "Physical activity"} icon={Dumbbell} color="#22D3EE" cardCls={cardCls} isDark={isDark}>
                        <div className="space-y-2">
                          {pathwayPreview.physical_activity_recommendations.map((pa, i) => (
                            <div key={i} className={`p-2.5 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{pa.type_bg}</span>
                                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isDark ? "bg-cyan-500/15 text-cyan-300" : "bg-cyan-50 text-cyan-700"}`}>{pa.frequency_bg}</span>
                              </div>
                              <p className={`text-[11px] mt-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>{pa.intensity_bg}</p>
                              <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{pa.why_bg}</p>
                            </div>
                          ))}
                        </div>
                      </Block>
                    )}

                    {(pathwayPreview.mindbody_recommendations || []).length > 0 && (
                      <Block title={bg ? "Mind-Body" : "Mind-Body"} icon={Brain} color="#F43F5E" cardCls={cardCls} isDark={isDark}>
                        <div className="space-y-2">
                          {pathwayPreview.mindbody_recommendations.map(m => (
                            <div key={m.id} className={`p-2.5 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                              <span className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{m.name_bg}</span>
                              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>
                                evidence {m.evidence_level}
                              </span>
                              <p className={`text-[11px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{m.dose_bg}</p>
                            </div>
                          ))}
                        </div>
                      </Block>
                    )}
                  </>
                )}
              </section>
            )}

            {/* STEP 6 — Pre-AI Summary */}
            {stepIdx === 5 && (
              <section className="space-y-3" data-testid="composer-step-summary-body">
                <div className={`${cardCls} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ClipboardList className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-700"}`} />
                    <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Преглед преди генериране" : "Pre-generation review"}
                    </h2>
                  </div>
                  <p className={`text-[12px] mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {bg ? "Това са всички данни, които ще получи AI асистентът. Прегледай преди генериране."
                       : "All data the AI will receive. Review before generation."}
                  </p>

                  {/* Selected phenotypes summary */}
                  <SummaryRow label={bg ? "Избрани субтипове / pathway-и" : "Selected subtypes / pathways"} isDark={isDark}>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedPhenotypes.map(k => (
                        <span key={k}
                          className={`text-[11px] px-2 py-0.5 rounded-full
                            ${isDark ? "bg-pink-500/15 text-pink-200" : "bg-pink-50 text-pink-800"}`}>
                          {(phenotypeConditions.find(c => c.key === k)?.label_bg)
                            || (enginePathways.find(p => p.key === k)?.label_bg) || k}
                        </span>
                      ))}
                    </div>
                  </SummaryRow>

                  <SummaryRow label={bg ? "Конфигурация на плана" : "Plan configuration"} isDark={isDark}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {[3, 5, 7, 10, 14].map(n => (
                        <button key={n} type="button" onClick={() => setPlanDays(n)}
                          className={`w-10 h-9 rounded-lg text-[12px] font-bold
                            ${planDays === n
                              ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow"
                              : (isDark ? "bg-white/5 text-gray-300 hover:bg-white/10"
                                       : "bg-gray-100 text-gray-700 hover:bg-gray-200")}`}
                          data-testid={`composer-days-${n}`}>{n}</button>
                      ))}
                      <span className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>{bg ? "дни" : "days"}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-1.5">
                      <Toggle Icon={Pill} color="#A855F7" label={bg ? "Добавки" : "Supplements"}
                        checked={includeSupplements} onChange={setIncludeSupplements} isDark={isDark} testId="composer-toggle-supplements" />
                      <Toggle Icon={Dumbbell} color="#22D3EE" label={bg ? "Усилия" : "Activity"}
                        checked={includePhysicalActivity} onChange={setIncludePhysicalActivity} isDark={isDark} testId="composer-toggle-pa" />
                      <Toggle Icon={Brain} color="#F43F5E" label={bg ? "Mind-Body" : "Mind-Body"}
                        checked={includeMindBody} onChange={setIncludeMindBody} isDark={isDark} testId="composer-toggle-mindbody" />
                    </div>
                  </SummaryRow>

                  {context && (
                    <>
                      <SummaryRow label={bg ? "Лична информация" : "Personal"} isDark={isDark}>
                        <span className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {context.personal?.gender || "—"}, {context.personal?.age || "—"} г · {context.body?.weight || "—"} kg / {context.body?.height || "—"} cm
                        </span>
                      </SummaryRow>
                      <SummaryRow label={bg ? "Здраве" : "Health"} isDark={isDark}>
                        <div className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {context.health?.currentConditions || "—"} · {bg ? "Лекарства: " : "Meds: "}{context.health?.medications || "—"}
                        </div>
                      </SummaryRow>
                      <SummaryRow label={bg ? "Лабораторни" : "Labs"} isDark={isDark}>
                        <span className={`text-[11px] font-mono ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {Object.entries(context.lab_values || {}).slice(0, 8).map(([k, v]) => `${k}=${v}`).join(", ") || "—"}
                        </span>
                      </SummaryRow>
                      <SummaryRow label="DIL" isDark={isDark}>
                        <span className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {bg ? "Хранения 7д: " : "Meals 7d: "}{context.dil?.meals_last_7d ?? 0} · {bg ? "Среден DIL: " : "Avg DIL: "}{context.dil?.avg_dil ?? "—"}
                        </span>
                      </SummaryRow>
                      {epigeneticData && (
                        <SummaryRow label={bg ? "Епигенетика" : "Epigenetics"} isDark={isDark}>
                          <span className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            Score: {epigeneticData.score || "—"} · Bio age: {epigeneticData.biological_age || "—"} · Δ {epigeneticData.age_acceleration || "—"}
                          </span>
                        </SummaryRow>
                      )}
                    </>
                  )}

                  <div className={`mt-4 p-3 rounded-xl text-[11px] flex items-start gap-2
                    ${isDark ? "bg-violet-500/10 text-violet-200 border border-violet-400/20"
                             : "bg-violet-50 text-violet-800 border border-violet-100"}`}>
                    <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{bg ? "На следващата стъпка AI асистентът (4 специалиста — NutriBot · EndoBot · HemoBot · NephroBot) ще генерира подробен персонализиран план."
                       : "Next step: AI consultation (4 specialists) generates a detailed personalized plan."}</span>
                  </div>
                </div>
              </section>
            )}

            {/* STEP 7 — AI plan */}
            {stepIdx === 6 && (
              <section className="space-y-3" data-testid="composer-step-ai-body">
                <div className={`${cardCls} p-4`}>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                      <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                        {bg ? "AI хранителен план — 4 специалиста" : "AI meal plan — 4 specialists"}
                      </h2>
                    </div>
                    <button onClick={handleAIGenerate} disabled={aiLoading}
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold shadow disabled:opacity-60"
                      data-testid="composer-ai-generate">
                      {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                      {aiResponse ? (bg ? "Регенерирай" : "Regenerate") : (bg ? "Генерирай" : "Generate")}
                    </button>
                  </div>
                  {chatHistory.length === 0 && !aiLoading && (
                    <p className={`text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {bg ? "Натисни 'Генерирай' — AI асистентът ще създаде подробен план на базата на прегледа."
                         : "Press Generate — AI builds a detailed plan from the pre-generation summary."}
                    </p>
                  )}
                  {chatHistory.length > 0 && (
                    <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="composer-ai-thread">
                      {chatHistory.map((m, i) => (
                        <div key={i}
                          className={`p-3 rounded-xl text-[12px] leading-relaxed whitespace-pre-wrap break-words
                            ${m.role === "user"
                              ? (isDark ? "bg-violet-500/10 text-violet-100 ml-8" : "bg-violet-50 text-violet-900 ml-8")
                              : (isDark ? "bg-white/5 text-gray-200 mr-8" : "bg-gray-50 text-gray-800 mr-8")}`}>
                          {m.text}
                        </div>
                      ))}
                      {aiLoading && <div className="flex items-center gap-2 text-xs text-gray-400 mr-8 p-2"><Loader2 className="w-4 h-4 animate-spin" />{bg ? "AI обмисля..." : "AI thinking..."}</div>}
                    </div>
                  )}
                  {aiSessionId && (
                    <div className="mt-3 flex items-end gap-2">
                      <textarea rows={2} value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                        placeholder={bg ? "Поискай корекция (напр. без глутен, по-малко въглехидрати)" : "Ask for adjustments..."}
                        className={`flex-1 p-2.5 rounded-xl text-sm border outline-none focus:ring-2 ring-violet-500/30
                          ${isDark ? "bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                                   : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
                        data-testid="composer-ai-input" />
                      <button onClick={handleChatSend} disabled={aiLoading || !chatInput.trim()}
                        className="h-10 px-4 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold disabled:opacity-50"
                        data-testid="composer-ai-send">
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {aiResponse && (
                  <div className={`${cardCls} p-4`}>
                    <h3 className={`text-sm font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{bg ? "Експорт" : "Export"}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <button onClick={handleSave}
                        className={`inline-flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-semibold border
                          ${isDark ? "bg-white/5 border-white/10 text-white hover:bg-white/10"
                                   : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50"}
                          ${savedPlanId ? "ring-2 ring-emerald-400/50" : ""}`}
                        data-testid="composer-save-btn">
                        <Save className="w-4 h-4" />
                        {savedPlanId ? (bg ? "Запазен ✓" : "Saved ✓") : (bg ? "Запази" : "Save")}
                      </button>
                      <button onClick={handlePdf} disabled={pdfDownloading}
                        className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold shadow disabled:opacity-60"
                        data-testid="composer-pdf-btn">
                        {pdfDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                        PDF
                      </button>
                      <button onClick={handleEmail} disabled={emailSending}
                        className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 text-white text-sm font-semibold shadow disabled:opacity-60"
                        data-testid="composer-email-btn">
                        {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        {bg ? "Изпрати" : "Email"}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Footer nav */}
            <div className="mt-6 flex items-center justify-between">
              <button type="button" onClick={() => setStepIdx(Math.max(0, stepIdx - 1))} disabled={stepIdx === 0}
                className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-sm font-semibold disabled:opacity-40
                  ${isDark ? "bg-white/5 text-gray-200 hover:bg-white/10" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                data-testid="composer-prev-btn">
                <ArrowLeft className="w-4 h-4" /> {bg ? "Назад" : "Back"}
              </button>
              <button type="button" onClick={goNext}
                disabled={!canGoNext || stepIdx === STEPS.length - 1}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold shadow disabled:opacity-40"
                data-testid="composer-next-btn">
                {bg ? "Напред" : "Next"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </main>
        </div>

        {/* iter322 — Formula transparency modal */}
        {formulasOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 bg-black/70 backdrop-blur-sm"
            onClick={() => setFormulasOpen(false)}
            data-testid="composer-formulas-modal">
            <div onClick={(e) => e.stopPropagation()}
              className={`max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl
                ${isDark ? "bg-[hsl(222,47%,9%)] border border-white/10" : "bg-white border border-gray-200"}`}>
              <header className="sticky top-0 px-5 py-3 flex items-center justify-between gap-3 backdrop-blur-md
                  border-b" style={{
                    background: isDark ? "hsla(222,47%,9%,0.95)" : "hsla(0,0%,100%,0.95)",
                    borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                  }}>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-cyan-500 to-violet-600 text-white">
                    <Calculator className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Формули & прозрачност" : "Formulas & Transparency"}
                    </h2>
                    <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {bg ? "Зеро халюцинации — всичко е детерминистично." : "Zero hallucinations — fully deterministic."}
                    </p>
                  </div>
                </div>
                <button onClick={() => setFormulasOpen(false)} aria-label="Close"
                  className={`p-1.5 rounded-lg ${isDark ? "hover:bg-white/5 text-gray-400" : "hover:bg-gray-100 text-gray-600"}`}
                  data-testid="composer-formulas-close">
                  <X className="w-5 h-5" />
                </button>
              </header>
              <div className="px-5 py-4 space-y-5">
                {!formulas && <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" />{bg ? "Зареждане..." : "Loading..."}</div>}
                {formulas && (
                  <>
                    <FormulaSection title="Source" isDark={isDark}>
                      <p className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>{formulas.source}</p>
                    </FormulaSection>

                    <FormulaSection title={bg ? "Body composition" : "Body composition"} isDark={isDark}>
                      <FormulaRow label="US Navy male" value={formulas.body_composition.us_navy_male} isDark={isDark} />
                      <FormulaRow label="US Navy female" value={formulas.body_composition.us_navy_female} isDark={isDark} />
                      <FormulaRow label="Deurenberg" value={formulas.body_composition.deurenberg_fallback} isDark={isDark} />
                      <FormulaRow label="Selection" value={formulas.body_composition.selection_rule} isDark={isDark} />
                      <FormulaRow label="Clamp" value={formulas.body_composition.clamp} isDark={isDark} />
                      <FormulaRow label="fat_mass" value={formulas.body_composition.fat_mass_kg} isDark={isDark} />
                      <FormulaRow label="lean_mass" value={formulas.body_composition.lean_mass_kg} isDark={isDark} />
                      <div className={`mt-2 text-[11px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                        {bg ? "Източници: " : "Refs: "}{formulas.body_composition.references.join(" · ")}
                      </div>
                    </FormulaSection>

                    <FormulaSection title={bg ? "Естрогенен риск" : "Estrogen risk"} isDark={isDark}>
                      <FormulaRow label="♀ high"     value={formulas.estrogen_risk.female.high}     isDark={isDark} />
                      <FormulaRow label="♀ moderate" value={formulas.estrogen_risk.female.moderate} isDark={isDark} />
                      <FormulaRow label="♀ low"      value={formulas.estrogen_risk.female.low}      isDark={isDark} />
                      <FormulaRow label="♂ high"     value={formulas.estrogen_risk.male.high}       isDark={isDark} />
                      <FormulaRow label="♂ moderate" value={formulas.estrogen_risk.male.moderate}   isDark={isDark} />
                      <FormulaRow label="♂ low"      value={formulas.estrogen_risk.male.low}        isDark={isDark} />
                      <p className={`text-[11px] mt-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{formulas.estrogen_risk.rationale}</p>
                    </FormulaSection>

                    <FormulaSection title={bg ? "Енергия / DIL" : "Energy / DIL"} isDark={isDark}>
                      <FormulaRow label="BMR (Katch-McArdle)" value={formulas.energy.bmr_katch_mcardle} isDark={isDark} />
                      <FormulaRow label="TDEE" value={formulas.energy.tdee} isDark={isDark} />
                      <div className={`text-[11px] mt-1 mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        {bg ? "Activity multipliers: " : "Activity multipliers: "}{Object.entries(formulas.energy.activity_multipliers).map(([k, v]) => `${k}=${v}`).join(", ")}
                      </div>
                      <FormulaRow label="TEF default" value={formulas.energy.tef_thermic_effect_of_food.default} isDark={isDark} />
                      <FormulaRow label="TEF IR/T2DM" value={formulas.energy.tef_thermic_effect_of_food.ir_or_t2dm} isDark={isDark} />
                      <div className={`text-[11px] mt-2 mb-1 font-bold uppercase ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        {bg ? "Goal calorie targets:" : "Goal calorie targets:"}
                      </div>
                      {Object.entries(formulas.energy.goal_calorie_targets).map(([k, v]) => (
                        <FormulaRow key={k} label={k} value={v} isDark={isDark} />
                      ))}
                      <FormulaRow label="Personalized DIL" value={formulas.energy.personalized_dil} isDark={isDark} />
                      <FormulaRow label="Reference DIL" value={formulas.energy.reference_dil} isDark={isDark} />
                      <FormulaRow label="Cycle modifier" value={formulas.energy.cycle_modifier} isDark={isDark} />
                    </FormulaSection>

                    <FormulaSection title={bg ? "Lab-based pathway derivation" : "Lab-based pathway derivation"} isDark={isDark}>
                      <p className={`text-[11px] mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{formulas.lab_based_pathway_derivation.purpose}</p>
                      {Object.entries(formulas.lab_based_pathway_derivation.thresholds_si_units).map(([k, v]) => (
                        <FormulaRow key={k} label={k} value={v} isDark={isDark} />
                      ))}
                      <p className={`text-[11px] mt-2 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                        ⚠ {formulas.lab_based_pathway_derivation.fallback_when_no_data}
                      </p>
                    </FormulaSection>

                    <FormulaSection title={bg ? "Phenotype subtype → pathway mapping" : "Phenotype subtype → pathway mapping"} isDark={isDark}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {Object.entries(formulas.phenotype_subtype_to_pathways).map(([k, v]) => (
                          <div key={k} className={`text-[11px] p-1.5 rounded ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                            <span className="font-mono font-bold">{k}</span> → {v.join(", ")}
                          </div>
                        ))}
                      </div>
                    </FormulaSection>

                    <FormulaSection title={bg ? "AI роля (без хипотези)" : "AI role (no hypotheses)"} isDark={isDark}>
                      <p className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>{formulas.ai_role}</p>
                    </FormulaSection>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DoctorShell>
    </>
  );
}

function Block({ title, icon: Icon, color, children, cardCls, isDark }) {
  return (
    <div className={`${cardCls} p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: color }}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Grid({ items, isDark, empty }) {
  if (!items || items.length === 0) {
    return <span className="text-xs text-gray-500">{empty || "—"}</span>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {items.map(([k, v], i) => (
        <div key={i} className={`p-2 rounded-lg text-[11px] ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
          <div className={`uppercase tracking-wider text-[9px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>{k}</div>
          <div className={`font-mono text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{v ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

function Toggle({ Icon, color, label, checked, onChange, isDark, testId }) {
  return (
    <label className={`flex items-center justify-between gap-1.5 p-2 rounded-lg cursor-pointer
      ${isDark ? "bg-white/5 hover:bg-white/10" : "bg-gray-50 hover:bg-gray-100"}`}
      data-testid={testId}>
      <span className="flex items-center gap-1.5 min-w-0">
        <span className="w-6 h-6 rounded-md flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: color }}>
          <Icon className="w-3 h-3" />
        </span>
        <span className={`text-[11px] font-semibold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{label}</span>
      </span>
      <span className={`relative inline-block w-8 h-5 rounded-full transition-colors flex-shrink-0
        ${checked ? "bg-violet-500" : (isDark ? "bg-white/10" : "bg-gray-300")}`}>
        <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
          ${checked ? "translate-x-3" : "translate-x-0"}`} />
      </span>
    </label>
  );
}

function SummaryRow({ label, children, isDark }) {
  return (
    <div className={`mb-3 pb-3 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
      <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>{label}</div>
      {children}
    </div>
  );
}

/**
 * SubtypeDetail — fenotype subtype data may have wildly different shapes
 * (PAM50: { code, subtype, markers: { ER, PR, HER2, Ki67 } }, MAFLD: { stage, fibrosis }, etc.)
 * Renders a compact one-liner.
 */
function SubtypeDetail({ data, isDark }) {
  if (!data) return null;
  if (typeof data === "string") {
    return <div className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>{data}</div>;
  }
  if (typeof data !== "object") return null;
  const code = data.code || data.subtype || data.name || "";
  const markers = data.markers || {};
  const markerStr = Object.entries(markers)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}: ${v === true ? "+" : v === false ? "−" : v}`).join(" · ");
  return (
    <div className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
      {code && <span className="font-mono">{code}</span>}
      {markerStr && <span className="ml-1.5">· {markerStr}</span>}
      {data.stage && <span className="ml-1.5">· stage {data.stage}</span>}
      {data.activity && <span className="ml-1.5">· {data.activity}</span>}
    </div>
  );
}


function FormulaSection({ title, isDark, children }) {
  return (
    <div className={`p-3 rounded-xl ${isDark ? "bg-white/5 border border-white/5" : "bg-gray-50 border border-gray-100"}`}>
      <h3 className={`text-[11px] uppercase tracking-wider font-bold mb-2 ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>{title}</h3>
      {children}
    </div>
  );
}

function FormulaRow({ label, value, isDark }) {
  return (
    <div className={`flex items-start gap-2 py-1 text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
      <span className="font-mono uppercase text-[10px] flex-shrink-0 w-32 truncate" title={label}>{label}</span>
      <span className="font-mono break-all">{value}</span>
    </div>
  );
}

